import React from 'react';

import { useTranslation } from 'react-i18next';
import { useParams, Navigate } from 'react-router-dom';

import { CsTagIcon, DotaTagIcon, LolTagIcon } from '@icons';
import { mapTournamentToQuest } from '@utils/mapTournamentToQuest';

import { useQueryTournaments } from '@hooks/api/tournaments';

import { GAME_TYPE } from '@types';

import { Button } from '@components/button';
import { Loader } from '@components/loader';
import Quest from '@components/quest/quest';

import * as Styled from './tournament-detail.styled';

const TournamentDetail: React.FC = () => {
	const { id } = useParams<{ id: string }>();
	const { t } = useTranslation(['main', 'notifications', 'tournaments']);
	const { data: events = [], isLoading } = useQueryTournaments();

	const eventIndex = id ? parseInt(id, 10) : null;
	const event = eventIndex !== null && events[eventIndex] ? events[eventIndex] : null;

	const formatDate = (dateString: Date | null): string => {
		if (!dateString) return '';

		const date = new Date(dateString);
		if (isNaN(date.getTime())) return '';

		return date.toLocaleDateString('ru-RU', {
			day: '2-digit',
			month: '2-digit',
			year: 'numeric'
		});
	};

	if (isLoading) {
		return (
			<Styled.Container>
				<Loader />
			</Styled.Container>
		);
	}

	if (!event) {
		return <Navigate to="/tournaments" replace />;
	}

	const renderGameInfo = () => {
		switch (event.gameType) {
			case GAME_TYPE.DOTA2:
				return (
					<Styled.ModalGameItem>
						<div><DotaTagIcon /></div>
						<div>Dota 2</div>
					</Styled.ModalGameItem>
				);
			case GAME_TYPE.CS2:
				return (
					<Styled.ModalGameItem>
						<div><CsTagIcon /></div>
						<div>CS 2</div>
					</Styled.ModalGameItem>
				);
			case GAME_TYPE.LOL:
				return (
					<Styled.ModalGameItem>
						<div><LolTagIcon /></div>
						<div>League of Legends</div>
					</Styled.ModalGameItem>
				);
			default:
				return (
					<Styled.ModalGameItem>
						<div></div>
						<div>{event.gameType}</div>
					</Styled.ModalGameItem>
				);
		}
	};

	return (
		<Styled.Background $backgroundImage={event.img}>
			<Styled.BackgroundInner>
				<Styled.Container>
					<Styled.Wrapper>
						<Styled.Info>
							<Styled.Title>
								{t('tournament')} {event.name}
							</Styled.Title>

							<Styled.ModalGame>
								{renderGameInfo()}
							</Styled.ModalGame>

							<Styled.Dates>
								<div>
									<div>{t('tournaments:date_start')}</div>
									<Styled.Date>
										{formatDate(event.startDate)}
									</Styled.Date>
								</div>
								<div>
									<div>{t('tournaments:date_end')}</div>
									<Styled.Date>
										{formatDate(event.endDate)}
									</Styled.Date>
								</div>
							</Styled.Dates>

							<Styled.Description
								dangerouslySetInnerHTML={{
									__html: event.description
								}}
							/>

							{event.url && event.url !== '#' ? (
								<Styled.RemoveEvents>
									<a href={event.url} target="_blank" rel="noopener noreferrer">
										<Button color="primary">
											{t('tournaments:go_to_matches')}
										</Button>
									</a>
								</Styled.RemoveEvents>
							) : (
								<Button color="dark">
									{t('tournaments:no_matches')}
								</Button>
							)}
						</Styled.Info>
					</Styled.Wrapper>
				</Styled.Container>

				{event.nameQuest && (
					<Styled.WrapperQuests>
						<Quest quest={mapTournamentToQuest(event, eventIndex || 0)} />
					</Styled.WrapperQuests>
				)}
			</Styled.BackgroundInner>
		</Styled.Background>
	);
};

export default TournamentDetail;